import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Button, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  isChunkError: boolean;
}

const RELOAD_KEY = 'app_chunk_reload_attempted';

/**
 * ErrorBoundary para errores de carga de chunks (React lazy / code splitting).
 * Si ocurre un ChunkLoadError intenta recargar automáticamente una vez.
 */
export class ChunkErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, isChunkError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    const msg = error?.message ?? '';

    const isChunkError =
      msg.includes('Loading chunk') ||
      msg.includes('ChunkLoadError') ||
      msg.includes('Failed to fetch dynamically imported module') ||
      msg.includes('ERR_ABORTED');

    return { hasError: true, isChunkError };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ChunkErrorBoundary:', error, errorInfo);

    const reloadAttempted = sessionStorage.getItem(RELOAD_KEY);

    // Si es error de chunk intentamos recargar una sola vez
    if (this.state.isChunkError && !reloadAttempted) {
      sessionStorage.setItem(RELOAD_KEY, 'true');
      window.location.reload();
    }
  }

  handleReload = () => {
    sessionStorage.removeItem(RELOAD_KEY);
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          p: 3,
          textAlign: 'center',
        }}
      >
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No se pudo cargar esta parte de la aplicación
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2, maxWidth: 520 }}
        >
          {this.state.isChunkError
            ? 'No se pudo cargar la aplicación. Intente recargar la página con Ctrl+Shift+R.'
            : 'Ha ocurrido un error inesperado.'}
        </Typography>

        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={this.handleReload}
        >
          Recargar página
        </Button>
      </Box>
    );
  }
}